<template>
  <!-- 仓库勾选商品的配送单预览弹框 -->
  <!-- 预览弹框按UI稿固定宽度1216px，小屏幕时自动收缩 -->
  <el-dialog :visible.sync="visible" :title="$t('message.warehouseDeliveryPreview')" width="1216px"
    append-to-body @close="close">
    <!-- 预览商品列表 -->
    <div v-loading="loading" class="delivery-preview-list">
      <!-- 自动追加辅料提示语 -->
      <div class="preview-warning">{{ $t('message.warehouseDeliveryPreviewWarning') }}</div>
      <!-- 顶部列标题与参考弹框保持一致 -->
      <div class="preview-table-header">
        <span>NO</span><span>{{ $t('message.orderNumber') }}</span><span>{{ $t('message.mash') }}</span>
        <span>{{ $t('message.photo') }}</span><span>{{ $t('message.product') }}</span><span>{{ $t('message.purchaseData') }}</span>
        <span>{{ $t('message.labelInformation') }}</span><span>{{ $t('message.warehouseAvailableCount') }}</span><span>{{ $t('message.shipmentQuantity') }}</span>
      </div>
    
      <div v-for="(item, itemIndex) in previewItems" :key="item.id" class="delivery-preview-item">
        <!-- 主商品信息 -->
        <div class="preview-main-row">
          <div class="preview-index">{{ itemIndex + 1 }}</div>
          <div class="preview-order-sn">{{ item.order_sn }}</div>
          <div>{{ item.sorting }}</div>
          <el-image class="preview-image" :src="item.pic" fit="cover">
            <!-- 主图加载失败占位 -->
            <div slot="error" class="preview-image-placeholder"><i class="el-icon-picture-outline" /></div>
          </el-image>
          <div class="preview-main-info preview-attributes">
            <!-- 商品详情列仅展示接口返回的商品属性 -->
            <p v-for="(attribute, attributeIndex) in item.detail" :key="'preview-detail-' + item.id + attributeIndex">
              {{ attribute.key }}：{{ attribute.value }}
            </p>
          </div>
          <div class="preview-purchase-info">
            <div>{{ $t('message.quantity') }}：{{ item.confirm_num }}</div>
            <div>{{ $t('message.unitPrice') }}：{{ item.confirm_price }}</div>
            <div>{{ $t('message.domesticFreight') }}：{{ item.confirm_freight }}</div>
            <div>{{ $t('message.subtotal') }}：{{ purchaseSubtotal(item) }}</div>
          </div>
          <div class="preview-tags">
            <div v-for="(tag, tagIndex) in item.order_detail_tag" :key="'preview-tag-' + item.id + tagIndex">
              {{ tag.type }}：{{ tag.no }}<br />{{ tag.goods_no }}
            </div>
          </div>
          <div class="preview-available">{{ item.send_may_num }}</div>
          <!-- 本次提出数量可编辑 -->
          <el-input v-model="item.prefill_num" class="preview-num-input" size="small" type="text"
            :placeholder="$t('message.shipmentQuantity')" @input="sanitizeNum(item)" @blur="validateNum(item)" />
        </div>

        <!-- 自动加载并展示当前商品绑定的辅料 -->
        <div v-if="item.materials.length" class="preview-material-list">
          <div v-for="material in item.materials" :key="material.id" class="preview-material-card"
           >
            <!-- 辅料选择框固定在卡片左上角 -->
            <el-checkbox v-model="material.checked" class="material-preview-checkbox"
              :disabled="isMaterialUnselectable(item, material)" />
            <el-image class="material-preview-image" :src="material.material.main_image" fit="cover">
              <!-- 辅料图片加载失败占位 -->
              <div slot="error" class="preview-image-placeholder"><i class="el-icon-picture-outline" /></div>
            </el-image>
            <div class="material-preview-info">
              <div class="material-preview-name">{{ material.material.name_trans || material.material.name }}</div>
              <div class="material-preview-type">{{ material.material.type && (material.material.type.name_trans || material.material.type.name) }}</div>
              <div>{{ $t('message.inventoryQuantity') }}：{{ material.material.material_stock_num }}</div>
              <div class="material-consume-line">
                {{ $t('message.materialConsumption') }}：
                <!-- 辅料消耗点数支持直接编辑 -->
                <el-input v-model="material.consume" class="material-consume-input"  type="text"
                  @input="sanitizeConsume(item, material)" size="small">
                  <!-- 消耗单位显示在输入框右侧，与参考图一致 -->
                  <template slot="append">{{ $t('message.pointsPerSet') }}</template>
                </el-input>
              </div>
            </div>
            <!-- 辅料库存状态与本次使用数量 -->
            <div class="material-status" :class="materialStatusClass(item, material)">{{ materialStatusText(item, material) }}</div>
            <span class="material-use-count">{{ $t('message.materialUsage') }}：{{ material.consume * item.prefill_num }}</span>
          </div>
        </div>
      </div>
    
      <!-- 没有勾选商品时的空状态 -->
      <div v-if="!loading && !previewItems.length" class="preview-empty">{{ $t('message.warehouseNoData') }}</div>
    </div>

    <!-- 预览底部操作 -->
    <div slot="footer" class="preview-footer">
      <el-button type="danger" @click="confirmPreview">{{ $t('message.confirmSubmission') }}</el-button>
      <el-button @click="close">{{ $t('message.cancel') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  // 仓库专用配送单预览组件
  name: 'WarehouseDeliveryPreview',
  data() {
    return {
      visible: false, // 弹框显示状态
      loading: false, // 辅料详情加载状态
      userId: '', // 客户主账号ID
      previewItems: [], // 当前勾选商品及其辅料
    }
  },
  methods: {
    // 打开预览并加载选中商品的绑定辅料
    open(details, userId) {
      this.userId = userId
      this.previewItems = (details || []).map(detail => ({
        ...detail,
        prefill_num: Number(detail.shipment_num || detail.send_may_num || 0),
        materials: [],
      }))
      this.visible = true
      this.loadMaterials()
    },
    // 调用绑定辅料详情接口，单次最多请求50个订单详情
    loadMaterials() {
      const items = this.previewItems.filter(item => item.has_material_bind == 1 && item.material_id == 0)
      if (!items.length) return
      this.loading = true
      // 每个商品独立请求一次，避免不同商品的辅料数据混入同一组
      Promise.all(items.map(item => this.$api.clienteleApi.stockMaterialBindList({
        user_id: this.userId,
        order_detail_ids: [item.id],
        max_ids: 50,
      }).then(res => {
        const group = (res.data || []).find(entry => String(entry.order_detail_id) === String(item.id))
        this.$set(item, 'materials', (group && group.material_bind || []).map(material => ({
          ...material,
          checked: !this.isMaterialUnselectable(item, material),
        })))
      }))).finally(() => {
        this.loading = false
      })
    },
    // 输入数量时仅保留正整数，并限制不超过可提出数量
    purchaseSubtotal(item) {
      return (Number(item.confirm_num || 0) * Number(item.confirm_price || 0) + Number(item.confirm_freight || 0)).toFixed(2)
    },
    sanitizeNum(item) {
      const digits = String(item.prefill_num == null ? '' : item.prefill_num).replace(/[^\d]/g, '')
      let value = digits === '' ? '' : parseInt(digits, 10)
      const max = Number(item.send_may_num || 0)
      if (value !== '' && value > max) value = max
      if (value === 0) value = ''
      this.$set(item, 'prefill_num', value)
      this.syncMaterialChecked(item)
    },
    // 数量失焦时恢复为合法值
    validateNum(item) {
      this.sanitizeNum(item)
      if (!item.prefill_num && Number(item.send_may_num || 0) > 0) {
        this.$set(item, 'prefill_num', Number(item.send_may_num))
      }
      this.syncMaterialChecked(item)
    },
    // 辅料消耗输入框仅允许输入非负整数
    sanitizeConsume(item, material) {
      const digits = String(material.consume == null ? '' : material.consume).replace(/[^\d]/g, '')
      this.$set(material, 'consume', digits === '' ? 0 : parseInt(digits, 10))
      this.syncMaterialChecked(item)
    },
    // 辅料当前库存
    materialStockNum(material) {
      return Number((material.material && material.material.material_stock_num) || 0)
    },
    // 本次提出实际需要的辅料数量
    materialRequiredNum(item, material) {
      return Number(material.consume || 0) * Number(item.prefill_num || 0)
    },
    // 无库存或库存不足以覆盖本次使用时禁止勾选
    isMaterialUnselectable(item, material) {
      const stock = this.materialStockNum(material)
      return stock <= 0 || stock < this.materialRequiredNum(item, material)
    },
    // 库存变化后同步取消不可选辅料的勾选
    syncMaterialChecked(item) {
      (item.materials || []).forEach(material => {
        if (this.isMaterialUnselectable(item, material) && material.checked) {
          this.$set(material, 'checked', false)
        }
      })
    },
    // 根据辅料实际库存显示库存状态
    materialStatusText(item, material) {
      const stock = this.materialStockNum(material)
      if (stock <= 0) return this.$t('message.noStock')
      return stock >= this.materialRequiredNum(item, material)
        ? this.$t('message.stockSufficient') : this.$t('message.stockInsufficient')
    },
    // 库存状态对应参考图颜色
    materialStatusClass(item, material) {
      return this.isMaterialUnselectable(item, material)
        ? 'material-status-error' : 'material-status-ok'
    },
    // 组装 porderPreview 所需 stock 参数并交给父页面跳转
    confirmPreview() {
      const stock = this.previewItems.map(item => ({
        order_detail_id: item.id,
        prefill_num: Number(item.prefill_num || 0),
        material_consume: item.materials.filter(material => material.checked && Number(material.consume) > 0)
          .map(material => ({
            material_id: material.material_id,
            consume: Number(material.consume),
          })),
      }))
      if (stock.some(item => item.prefill_num <= 0)) {
        this.$message.error(this.$t('message.warehouseShipmentQuantityInvalid'))
        return
      }
      this.$emit('confirm', stock)
      this.close()
    },
    // 关闭预览弹框并清理数据
    close() {
      this.visible = false
      this.previewItems = []
    },
  },
}
</script>

<style lang="scss" scoped>
// 预览商品列表
.delivery-preview-list {
  max-height: none;
  margin: 0 12px;
  overflow: visible;
  max-height: 600px;
  overflow-y: auto;
}

// 弹框宽度适配：保持UI稿1216px，同时避免小屏幕横向溢出
/deep/ .el-dialog {
  max-width: calc(100vw - 32px);
}

// 标题区域按参考图居中并压缩上下留白
/deep/ .el-dialog__header {
  padding: 18px 20px 12px;
  text-align: center;
}

// 关闭按钮保持参考图中的紧凑尺寸
/deep/ .el-dialog__headerbtn {
  top: 16px;
  right: 16px;
}

// 自动追加辅料警示条
.preview-warning {
  margin-bottom: 12px;
  padding: 6px 10px;
  background: #fff1f0;
  color: #c7252b;
  font-size: 14px;
  height: 22px;
}

// 预览列表表头
.preview-table-header {
  display: grid;
  grid-template-columns: 4% 18% 6% 9% 17% 16% 14% 8% 8%;
  align-items: center;
  min-height: 50px;
  padding: 0 10px;
  background: #eef0f5;
  color: #606266;
  font-size: 14px;
  text-align: center;
  margin-bottom: 10px;
  position: sticky;
  top: 0;
  z-index: 999;
}

// 商品主信息行
.delivery-preview-item {
  margin-bottom: 12px;
  padding: 0;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background-color: #f8f8f8;
}

.preview-main-row {
  display: grid;
  grid-template-columns: 4% 18% 6% 9% 17% 16% 14% 8% 8%;
  align-items: center;
  min-height: 96px;
  padding: 10px;
  box-sizing: border-box;
  text-align: center;
}

.preview-image {
  justify-self: center;
  width: 50px;
  height: 50px;
  background: #9b9b9b;
}

.preview-main-info {
  min-width: 0;
  font-size: 14px;
  text-align: left;
  line-height: 22px;
}

.preview-order-sn {
  color: #c7252b;
  font-weight: 600;
  text-decoration: underline;
}

.preview-purchase-info,
.preview-tags,
.preview-attributes {
  min-width: 0;
  font-size: 14px;
  line-height: 18px;
  text-align: left;
}

.preview-purchase-info p,
.preview-attributes p {
  margin: 0;
}

.preview-index {
  text-align: center;
}

.preview-available {
  white-space: nowrap;
}

.preview-num-input {
  width: 86px;
}

// 辅料卡片区域
.preview-material-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
  gap: 20px;
  margin-top: 14px;
  padding: 28px 18px 16px;
  border-top: 1px solid #ebeef5;
}

.preview-material-card {
  position: relative;
  display: flex;
  flex: 0 0 304px;
  align-items: flex-start;
  width: 304px;
  height: 205px;
  min-height: 205px;
  gap: 8px;
  padding: 14px 12px 50px 45px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  box-sizing: border-box;
  background-color: #fff;
 
}

// 辅料卡片底部信息分隔线
.preview-material-card::after {
  position: absolute;
  right: 12px;
  bottom: 48px;
  left: 12px;
  border-top: 1px solid #ebeef5;
  content: '';
}

// 辅料选择框固定在卡片左上角
.material-preview-checkbox {
  position: absolute;
  top: 12px;
  left: 16px;
  transform: scale(1.3);
}

.material-preview-image {
  width: 80px;
  height: 80px;
  border-radius: 4px;
}

.material-preview-info {
  flex: 1;
  min-width: 0;
  color: #606266;
  font-size: 14px;
  line-height: 20px;
}

.material-preview-name {
  overflow: hidden;
  color: #333;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.material-preview-type {
  color: #409eff;
  margin: 8px 0;
  height: 24px;
  border-radius: 4px;
  background-color: #EBF2FF;
  padding: 0 8px;
  display: inline-block;
  line-height: 24px;
}

// 辅料消耗输入框
.material-consume-input {
  
  width: 100px;
 

  /deep/ .el-input-group__append {
   width: 30px;
   padding: 0 5px;
    background-color: #fff;
    line-height: 28px;
  }
}

// 消耗标签、输入框和单位保持同一行
.material-consume-line {
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin-top: 8px;
}

// 辅料库存状态标签
.material-status {
  position: absolute;
  bottom: 12px;
  left: 12px;
  padding: 2px 5px;
  border-radius: 2px;
  font-size: 12px;
}

.material-status-ok {
  background: #e1f3d8;
  color: #67c23a;
}

.material-status-error {
  background: #fde2e2;
  color: #f56c6c;
}

.material-use-count {
  position: absolute;
  right: 12px;
  bottom: 16px;
  white-space: nowrap;
}

.preview-empty {
  padding: 40px;
  color: #909399;
  text-align: center;
}

.preview-footer {
  text-align: center;
}

// 辅料卡片重构：使用Flex排版还原UI稿，避免固定坐标影响自适应
.preview-material-list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
  padding: 16px 18px;
}





</style>
